import type { UnknownObj } from '@repo/internal-tools-type-utils/src/primitives.ts'

type IDeepMergeTwoOptions = {
  mutate?: boolean
}

const isObject = <T extends UnknownObj>(value: T) =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

export const deepMergeTwo = <T1 extends UnknownObj, T2 extends UnknownObj>(
  targetObject: T1,
  sourceObject: T2,
  { mutate = false }: IDeepMergeTwoOptions = {},
): UnknownObj => {
  if (!sourceObject) {
    return targetObject
  }

  if (!isObject(targetObject) || !isObject(sourceObject)) {
    throw new Error('deepMerge: target and source must be objects')
  }

  let newObject: UnknownObj
  if (mutate) {
    newObject = Object.assign(targetObject, sourceObject)
  } else {
    newObject = Object.assign({}, targetObject, sourceObject)
  }

  Object.entries(sourceObject).forEach(([sourceKey, sourceValue]) => {
    const targetValue = targetObject?.[sourceKey] as UnknownObj
    const transformSourceValue = sourceValue as UnknownObj
    if (sourceValue === undefined) {
      newObject[sourceKey] = targetValue
    } else if (isObject(targetValue) && isObject(transformSourceValue)) {
      newObject[sourceKey] = deepMergeTwo(targetValue, transformSourceValue)
    }
  })

  return newObject
}

export const deepMerge = (...objects: Array<UnknownObj | undefined>) => {
  return [{}]
    .concat(objects || {})
    .reduce((a, b) => deepMergeTwo(a, b, { mutate: false }))
}

export default deepMerge
